import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { ApiError, apiFetch } from "./api";
import { makeRefreshCoordinator } from "./refreshCoordinator";

// Single active session PER AUDIENCE. The access token lives in MEMORY ONLY — never in localStorage or
// sessionStorage, never in a JS-readable cookie. The refresh token rides an HttpOnly cookie the JS
// never sees. On app start we rehydrate the access token from that cookie, so a reload keeps the user
// signed in without a token ever touching web storage. A revoked/expired session lands the user back
// on sign-in with a plain reason, never a silent logout or white screen.
//
// Audience separation (Step C Part 4, hardened for Fix 2): the staff console (/console) and the owner
// desk (/desk) each mount their OWN <AuthProvider audience=…> (see App.js). The audience is passed as a
// PROP by the route — it is NOT read from window.location at module load, so it can never go stale when
// one browser is used for both surfaces. Each audience has its own session state AND its own refresh
// coordinator (own endpoint, own HttpOnly cookie, own BroadcastChannel, own Web Lock). Consequences the
// tests pin: an owner refresh can never mint or overwrite a staff token, and a staff refresh can never
// touch an owner token — in one browser, in either order.
const AuthCtx = createContext(null);

export const AUDIENCE_CFG = {
  owner: { refresh: "/identity/token/refresh",       signout: "/identity/token/refresh/logout",       chan: "juno-owner-auth-v1", lock: "juno-owner-refresh" },
  staff: { refresh: "/identity/staff/token/refresh", signout: "/identity/staff/token/refresh/logout", chan: "juno-staff-auth-v1", lock: "juno-staff-refresh" },
};

// One coordinator per audience, cached at module scope so it is created exactly once and NEVER torn
// down — the same StrictMode-safe lifetime the single-flight design relies on (a per-mount coordinator
// would have its BroadcastChannel closed by StrictMode's dev mount/unmount/mount and then throw).
const _coords = new Map();
export function coordFor(audience) {
  if (_coords.has(audience)) return _coords.get(audience);
  const cfg = AUDIENCE_CFG[audience] || AUDIENCE_CFG.owner;
  const coord = makeRefreshCoordinator({
    locks: (typeof navigator !== "undefined" && navigator.locks) || null,
    channel: typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(cfg.chan) : null,
    rotate: () => apiFetch(cfg.refresh, { method: "POST" }),
    lockName: cfg.lock,
    chan: cfg.chan,
  });
  _coords.set(audience, coord);
  return coord;
}

export function AuthProvider({ audience = "owner", children }) {
  const [session, setSession] = useState(null); // {access, sid, account_id} — access in memory only
  const [reason, setReason] = useState("");
  const [booting, setBooting] = useState(true);  // true until the app-start rehydrate settles
  const coord = coordFor(audience);

  const endSession = useCallback((why) => {
    setSession(null);
    setReason(why || "Your session has ended. Please sign in again.");
  }, []);

  // Map a failed refresh to the honest reason the owner sees on the sign-in screen. A supersede (this
  // session ended by a later sign-in, often on another host) gets its own words (F44 dir.1).
  const _endReason = (e) =>
    (e && e.detail === "session_superseded")
      ? "You signed in somewhere else. Sign in again here."
      : "Your session ended. Sign in again.";

  // Rehydrate on app start through the shared single-flight refresh, so React 18 StrictMode's
  // double-invoked effect (and any concurrent tab/401) share ONE rotate and never trip reuse->revoke.
  useEffect(() => {
    let live = true;
    coord.refreshOnce()
      .then((next) => { if (live && next) setSession({ access: next.access, sid: next.sid, account_id: next.account_id }); })
      .catch((e) => { if (live && e && e.detail === "session_superseded") setReason(_endReason(e)); })
      .finally(() => { if (live) setBooting(false); });
    return () => { live = false; };
  }, [coord]);

  // Receive cross-tab events: another tab rotated (adopt its access token) or the session ended.
  useEffect(() => {
    return coord.subscribe({
      onToken: ({ access, sid, account_id }) =>
        setSession((s) => (s ? { ...s, access, sid: sid || s.sid, account_id: account_id || s.account_id } : s)),
      onEnd: () => endSession("Your session was ended. Please sign in again."),
    });
  }, [coord, endSession]);

  // SIGN OUT (F142, 12 Oct 2026). Posts to the audience's cookie-scoped sign-out route, NOT to
  // /identity/logout: the refresh cookie is Path-scoped to the refresh endpoint, so the browser only
  // sends it to a path underneath that one. That is what lets the server end the session when the
  // in-memory access token has already expired — the bug this replaces: a ten-minute-old desk signed
  // out "successfully" in the UI while the server session and the cookie both lived on, so F5 walked
  // back in. The bearer still rides along for a break-glass session, which has no refresh cookie.
  // The local session is cleared whatever the server says, and a failure is shouted into the console
  // rather than swallowed — but it can never leave the user looking signed in.
  const logout = useCallback(async (why) => {
    const cfg = AUDIENCE_CFG[audience] || AUDIENCE_CFG.owner;
    try {
      await apiFetch(cfg.signout, { method: "POST", token: session?.access });
    } catch (e) {
      console.error("SIGN-OUT request failed; clearing this browser anyway", e);
    }
    coord.broadcastEnded();           // sign the other tabs of THIS audience out too (Step C Part 3)
    setSession(null);
    setReason(why || "You signed out.");
  }, [audience, coord, session]);

  // Authenticated call with ONE refresh-rotation attempt on 401; on failure the session ends honestly.
  // The caller's typed form state stays in React memory (never web storage), so a failed submit keeps
  // the text on screen for a re-try after sign-in (Step C Part 6).
  const authedFetch = useCallback(
    async (path, opts = {}) => {
      const s = session;
      if (!s) throw new ApiError(401, "no session");
      try {
        return await apiFetch(path, { ...opts, token: s.access });
      } catch (e) {
        if (e.status !== 401) throw e;
        try {
          const next = await coord.refreshOnce();
          const rotated = { ...s, access: next.access, sid: next.sid || s.sid,
                            account_id: next.account_id || s.account_id };
          setSession(rotated);
          return await apiFetch(path, { ...opts, token: rotated.access }); // exactly one retry
        } catch (e) {
          endSession(_endReason(e));
          throw new ApiError(401, "session ended");
        }
      }
    },
    [coord, session, endSession]
  );

  const value = { session, reason, booting, audience, setSession, endSession, logout, authedFetch,
                  clearReason: () => setReason("") };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
